import { CSP_NONCE, inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';

const HEAP_CONFIG_URL = 'https://cdn.eu.heap-api.com/config';

const HEAP_METHODS = [
  'init',
  'startTracking',
  'stopTracking',
  'track',
  'resetIdentity',
  'identify',
  'identifyHashed',
  'getSessionId',
  'getUserId',
  'getIdentity',
  'addUserProperties',
  'addEventProperties',
  'removeEventProperty',
  'clearEventProperties',
  'addAccountProperties',
  'addAdapter',
  'addTransformer',
  'addTransformerFn',
  'onReady',
  'addPageviewProperties',
  'removePageviewProperty',
  'clearPageviewProperties',
  'trackPageview',
] as const;

type HeapMethod = (typeof HEAP_METHODS)[number];

export type Heap = Partial<Record<HeapMethod, (...args: unknown[]) => unknown>> & {
  envId?: string;
  clientConfig?: Record<string, unknown>;
};

interface HeapReadyCallback {
  name: HeapMethod;
  fn: () => void;
}

export interface HeapWindow extends Window {
  heap?: Heap;
  heapReadyCb?: HeapReadyCallback[];
}

/**
 * Loads the Heap web SDK (https://developers.heap.io/docs/web).
 * TypeScript port of the official install snippet so the script can be injected with the CSP nonce.
 */
@Injectable({ providedIn: 'root' })
export class HeapService {
  private readonly cspNonce = inject(CSP_NONCE, { optional: true });

  load(): void {
    const appId = environment.heapAppId;
    const win = window as HeapWindow;
    if (!appId || win.heap?.envId) return;

    win.heapReadyCb = win.heapReadyCb || [];
    const heap: Heap = (win.heap = win.heap || ([] as unknown as Heap));
    heap.envId = appId;
    heap.clientConfig = { shouldFetchServerConfig: false };

    HEAP_METHODS.forEach(name => {
      heap[name] = (...args: unknown[]) => {
        win.heapReadyCb?.push({
          name,
          fn: () => win.heap?.[name]?.(...args),
        });
      };
    });

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `${HEAP_CONFIG_URL}/${appId}/heap_config.js`;
    if (this.cspNonce) {
      script.setAttribute('nonce', this.cspNonce);
    }
    document.head.appendChild(script);
  }
}
